import countryFoodPreferences from '../utils/country_food_preferences.json';
import axios from 'axios';

// List of supported countries - currently only Spain is enabled
export const SUPPORTED_COUNTRIES = ['Spain'];

// Default country to use as fallback (and currently the only enabled country)
export const DEFAULT_COUNTRY = 'Spain';

// Local storage key for persisting country selection
export const COUNTRY_STORAGE_KEY = 'urbaneats_country';

/**
 * Normalize country names to handle variations and case sensitivity
 */
export const normalizeCountryName = (country) => {
  if (!country || typeof country !== 'string') return DEFAULT_COUNTRY;
  
  // Common variations mapped to standard names
  const countryMap = {
    'spain': 'Spain',
    'españa': 'Spain',
    'deutschland': 'Germany',
    'frankreich': 'France',
    'francia': 'France',
    'italia': 'Italy',
    'uk': 'United Kingdom',
    'great britain': 'United Kingdom',
  };
  
  // Try to map from lowercase version
  const normalized = countryMap[country.toLowerCase()];
  if (normalized) {
    // Only return if it's a supported country, otherwise fall back to default
    return SUPPORTED_COUNTRIES.includes(normalized) ? normalized : DEFAULT_COUNTRY;
  }
  
  // If not in map, check if it's a case-insensitive match with supported countries
  const matchedCountry = SUPPORTED_COUNTRIES.find(
    c => c.toLowerCase() === country.toLowerCase()
  );
  if (matchedCountry) return matchedCountry;
  
  // No match found, return default
  return DEFAULT_COUNTRY;
};

/**
 * Detect country using IP-based geolocation
 */
export const detectCountryFromIP = async () => { 
  try {
    const response = await axios.get('https://ipwho.is/?fields=country');
    
    if (response.data && response.data.country) {
      const country = response.data.country;
      console.log(`Country detected by IP: ${country}`);
      return normalizeCountryName(country);
    }
    return null;
  } catch (error) {
    console.warn('Error detecting country from IP:', error);
    return null;
  }
};

/**
 * Check if a country is supported by our food preferences database
 */
export const isCountrySupported = (country) => {
  if (!country || typeof country !== 'string') return false;
  
  // Check exact match first
  if (SUPPORTED_COUNTRIES.includes(country)) {
    return true;
  }
  
  // Try normalized version
  const normalizedCountry = normalizeCountryName(country);
  return SUPPORTED_COUNTRIES.includes(normalizedCountry);
};

/**
 * Get measurement system for a specific country
 */
export const getCountryMeasurementSystem = (country) => {
  // Normalize the country name
  const normalizedCountry = normalizeCountryName(country);
  
  // Get country food preferences which may contain measurement system info
  const preferences = countryFoodPreferences[normalizedCountry] || countryFoodPreferences[DEFAULT_COUNTRY];
  
  // Check if preferences have measurement system defined
  if (preferences && preferences.measurementSystem) {
    return preferences.measurementSystem.toLowerCase();
  }
  
  // If not defined in preferences, use the utility function
  return usesMetricSystem(normalizedCountry) ? 'metric' : 'imperial';
};

/**
 * Check if a country uses the metric system
 */
export const usesMetricSystem = (country) => {
  // List of countries using imperial system (primarily)
  const imperialCountries = ['USA', 'United States', 'US'];
  
  // Normalize the country name
  const normalizedCountry = normalizeCountryName(country);
  
  return !imperialCountries.includes(normalizedCountry);
};

/**
 * Get currency information for a country
 */
export const getCountryCurrency = (country) => {
  // Normalize the country name
  const normalizedCountry = normalizeCountryName(country);
  
  const currencyMap = {
    'Spain': { code: 'EUR', symbol: '€' },
    'Germany': { code: 'EUR', symbol: '€' },
    'France': { code: 'EUR', symbol: '€' },
    'Italy': { code: 'EUR', symbol: '€' },
    'Portugal': { code: 'EUR', symbol: '€' },
    'Greece': { code: 'EUR', symbol: '€' },
    'Ireland': { code: 'EUR', symbol: '€' },
    'Estonia': { code: 'EUR', symbol: '€' },
    'Latvia': { code: 'EUR', symbol: '€' },
    'Lithuania': { code: 'EUR', symbol: '€' },
    'United Kingdom': { code: 'GBP', symbol: '£' },
    'USA': { code: 'USD', symbol: '$' },
    'Canada': { code: 'CAD', symbol: '$' },
    'Hungary': { code: 'HUF', symbol: 'Ft' }
  };
  
  return currencyMap[normalizedCountry] || currencyMap[DEFAULT_COUNTRY];
};